import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, ShieldCheck, AlertCircle, Info } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { COLORS } from "../../utils/constants";
import { UrssafConnectLogo, UrssafStatutInfo, UrssafCotisationsInfo } from "./UrssafBrandingKit";

/**
 * Modale d'onboarding Urssaf TDAE :
 * - Renseigne SIRET + périodicité
 * - Consent explicite pour le mandat de tierce déclaration
 * - Appelle POST /api/urssaf/mandat
 */
export const UrssafSetupModal = ({ open, onClose, onSuccess, initialSiret = "", initialPeriodicite = "T", initialDateNaissance = "" }) => {
  const api = useApi();
  const [siret, setSiret] = useState(initialSiret || "");
  const [dateNaissance, setDateNaissance] = useState(initialDateNaissance || "");
  const [periodicite, setPeriodicite] = useState(initialPeriodicite || "T");
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);

  useEffect(() => {
    if (open) {
      api.get("/urssaf/status").then(r => setEnvInfo(r.data)).catch(() => {});
      // Pré-remplit avec le profil courant si dispo
      api.get("/auth/me").then(r => {
        if (!initialSiret && r.data?.siret) setSiret(r.data.siret);
        if (!initialDateNaissance && r.data?.date_naissance) setDateNaissance(r.data.date_naissance);
      }).catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  const cleanSiret = siret.replace(/\s/g, "");
  const isValidSiret = /^\d{14}$/.test(cleanSiret);
  const isValidDob = /^\d{4}-\d{2}-\d{2}$/.test(dateNaissance);
  const canSubmit = isValidSiret && isValidDob && accept && !loading;

  const submit = async () => {
    setLoading(true);
    try {
      // Persiste d'abord SIRET + date_naissance sur le profil
      await api.put("/profile", { siret: cleanSiret, date_naissance: dateNaissance });
      // Puis crée le mandat TDAE
      await api.post("/urssaf/mandat", {
        siret: cleanSiret,
        date_naissance: dateNaissance,
        periodicite,
        accepte_mandat: true,
      });
      toast.success("Compte Urssaf connecté avec succès");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || "Erreur lors de la connexion";
      toast.error(typeof msg === "string" ? msg : "Erreur Urssaf");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="urssaf-setup-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b" style={{backgroundColor: COLORS.bg}}>
          <div className="flex items-center gap-3">
            <UrssafConnectLogo size={40} />
            <div>
              <h2 className="text-xl font-bold" style={{color: COLORS.text}}>Connecter mon compte Urssaf</h2>
              <p className="text-xs text-muted-foreground">Mandat de tierce déclaration</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:opacity-70" data-testid="urssaf-setup-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {envInfo?.env === "sandbox" && (
            <div className="p-3 rounded-lg flex items-start gap-2 text-sm" style={{backgroundColor: "#FEF3C7", color: "#92400E"}}>
              <Info size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>Environnement Sandbox Urssaf</strong> — Ta déclaration est réellement transmise à l'Urssaf en environnement de test.
              </div>
            </div>
          )}
          {envInfo?.mock_mode && (
            <div className="p-3 rounded-lg flex items-start gap-2 text-sm" style={{backgroundColor: "#FEF3C7", color: "#92400E"}}>
              <Info size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>Mode démo</strong> — Tes actions sont simulées mais toute la logique fonctionne.
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1" style={{color: COLORS.text}}>SIRET</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={17}
              value={siret}
              onChange={(e) => setSiret(e.target.value)}
              placeholder="123 456 789 01234"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{borderColor: isValidSiret ? COLORS.success : (siret ? COLORS.error : "#e5e7eb")}}
              data-testid="urssaf-setup-siret-input"
            />
            <p className="text-xs text-muted-foreground mt-1">14 chiffres, sans espace ni tiret</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{color: COLORS.text}}>Date de naissance</label>
            <input
              type="date"
              value={dateNaissance}
              onChange={(e) => setDateNaissance(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{borderColor: isValidDob ? COLORS.success : (dateNaissance ? COLORS.error : "#e5e7eb")}}
              data-testid="urssaf-setup-dob-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Requise par l'Urssaf pour créer le mandat de télédéclaration</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{color: COLORS.text}}>Périodicité de déclaration</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPeriodicite("M")}
                className="p-3 rounded-lg border-2 text-sm font-medium transition-all"
                style={{
                  borderColor: periodicite === "M" ? COLORS.accent : "#e5e7eb",
                  backgroundColor: periodicite === "M" ? COLORS.accent + "15" : "white",
                  color: periodicite === "M" ? COLORS.accent : COLORS.text,
                }}
                data-testid="urssaf-setup-periodicite-M"
              >
                Mensuel
              </button>
              <button
                onClick={() => setPeriodicite("T")}
                className="p-3 rounded-lg border-2 text-sm font-medium transition-all"
                style={{
                  borderColor: periodicite === "T" ? COLORS.accent : "#e5e7eb",
                  backgroundColor: periodicite === "T" ? COLORS.accent + "15" : "white",
                  color: periodicite === "T" ? COLORS.accent : COLORS.text,
                }}
                data-testid="urssaf-setup-periodicite-T"
              >
                Trimestriel
              </button>
            </div>
          </div>

          <div className="p-4 rounded-lg" style={{backgroundColor: COLORS.bg}}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                className="mt-1"
                data-testid="urssaf-setup-consent"
              />
              <div className="text-sm" style={{color: COLORS.text}}>
                <p className="font-medium mb-1">Je donne mandat à Biz360</p>
                <p className="text-muted-foreground text-xs">
                  Je mandate Biz360 pour transmettre mes déclarations de chiffre d'affaires
                  à l'Urssaf en mon nom. Je peux révoquer ce mandat à tout moment depuis mon
                  compte. Aucun paiement n'est prélevé sans mon accord explicite pour chaque déclaration.
                </p>
              </div>
            </label>
          </div>

          {/* Encarts obligatoires du kit éditorial ACOSS */}
          <UrssafStatutInfo />
          <UrssafCotisationsInfo />
        </div>

        <div className="p-6 border-t flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border hover:bg-gray-50"
            data-testid="urssaf-setup-cancel"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="flex-1 py-2 rounded-lg text-white font-medium transition-opacity"
            style={{
              backgroundColor: COLORS.accent,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
            data-testid="urssaf-setup-submit"
          >
            {loading ? "Connexion..." : "Connecter mon compte"}
          </button>
        </div>
      </div>
    </div>
  );
};
